import { XbotAnswerToQuestion, XbotBinding, XbotChatRequest, XbotHistory, XbotHistoryMessage, XbotMessage, XbotPageContext, XbotStarters } from "@dto/pyxbot-agent.dto";
import { Draft, SentDraft } from "../suggestions/draft";
import { xbotPageContext } from "./chatClient";
import type { XbotTurn } from "./turnState";

// Plain data in and out for the conversation: what goes out with each message, and what gets written to
// the browser's per-tab history.

/** Text is already final here (see finalizeDraft): trimmed once, within DRAFT_MESSAGE_MAX, picks measured against it. */
export interface XbotOutgoing {
  text: string;
  answerTo?: XbotAnswerToQuestion | null;
  /** The objects picked for names in the text, ranges in the text as it is here. */
  bindings?: XbotBinding[];
}

// Text is sent unmodified (trimming here would desync it from the picked ranges; the service stores it
// byte for byte). Bindings are omitted entirely when empty, so the body still matches an older server's shape.
export function xbotChatBody(
  outgoing: XbotOutgoing,
  threadId: string,
  turnToken: string,
  page: XbotPageContext | null | undefined,
): XbotChatRequest {
  const answerTo = outgoing.answerTo ?? null;
  const bindings = !answerTo && outgoing.bindings?.length ? outgoing.bindings : null;
  return {
    message: answerTo ? "" : outgoing.text || "",
    thread_id: threadId,
    turn_token: turnToken,
    page_context: xbotPageContext(page),
    answer_to: answerTo,
    ...(bindings ? { bindings } : {}),
  };
}

export function hasSomethingToSend(outgoing: XbotOutgoing): boolean {
  return Boolean(outgoing.answerTo) || Boolean((outgoing.text || "").trim());
}

// Plain-text fallback of an answer, for the history list and for readers without the drawn blocks.
export function answerText(message: XbotMessage | null | undefined): string {
  if (!message) {
    return "";
  }
  const blocks = Array.isArray(message.blocks) ? message.blocks : [];
  const sentences = blocks
    .map((block) => (typeof block?.fallback_text === "string" ? block.fallback_text.trim() : ""))
    .filter((sentence) => sentence.length > 0);
  if (sentences.length) {
    return sentences.join("\n\n");
  }
  return message.error?.message?.trim() || "";
}

// Strips claims/trace/usage before storing - the page never draws them and tab storage is small; blocks stay whole.
export function storedXbotMessage(message: XbotMessage): XbotMessage {
  return { ...message, claims: [], trace: [], usage: null };
}

export function assistantEntry(message: XbotMessage): XbotHistoryMessage {
  return { role: "assistant", message: answerText(message), xbot: storedXbotMessage(message) };
}

export function userEntry(shown: string): XbotHistoryMessage {
  return { role: "user", message: shown };
}

/** The agent message an entry of the history was drawn from, when it has one. */
export function xbotOf(entry: XbotHistoryMessage | null | undefined): XbotMessage | null {
  const xbot = entry?.xbot;
  return xbot && typeof xbot === "object" && Array.isArray(xbot.blocks) ? xbot : null;
}

export function newXbotConversation(localId: string, threadId: string, updated: string): XbotHistory {
  return { localId, threadId, topic: "New chat", messages: [], updated };
}

// Only for an empty conversation: reuses the thread id the server allocated for the starters request.
export function threadAfterStarters(
  conversation: XbotHistory | null | undefined,
  starters: XbotStarters | null | undefined,
): string | null {
  const given = typeof starters?.thread_id === "string" ? starters.thread_id.trim() : "";
  if (!conversation || !given || conversation.messages?.length) {
    return null;
  }
  return given;
}

// Tracks threads that hold a server-side key (asked something) and haven't been closed yet.
export class OpenThreads {
  private readonly open = new Set<string>();

  asked(threadId: string | null | undefined): void {
    if (threadId) {
      this.open.add(threadId);
    }
  }

  /** true when the thread had a key to give back */
  leave(threadId: string | null | undefined): boolean {
    return Boolean(threadId) && this.open.delete(threadId);
  }

  leaveAll(): string[] {
    const all = [...this.open];
    this.open.clear();
    return all;
  }
}

/** "error" is a refusal shown in place of an answer; it is never stored. */
export type XbotChatItemRole = "user" | "assistant" | "error";

export interface XbotChatItem {
  key: string;
  role: XbotChatItemRole;
  content: string;
  /** absent on an answer saved without one */
  message?: XbotMessage | null;
  pending?: boolean;
  /** turn as built so far; followed turns only */
  turn?: XbotTurn | null;
  /** lets a refused message with picked objects be restored into the editor with one press */
  restore?: Draft;
  /** A person's line as it was sent (text, picked objects, the question it answers): what Try again sends again. */
  sent?: XbotOutgoing;
  /**
   * The draft it was sent from, kept beside `sent` because the wire shape has no words for the picked line. Try
   * again hands it back, so a refusal of the retry can restore the picks just as a refusal of the first send does.
   */
  draft?: SentDraft;
}

// No-op if the pending line already settled or the list moved on without it.
export function withTurn(items: XbotChatItem[], pendingKey: string, turn: XbotTurn | null): XbotChatItem[] {
  const at = items.findIndex((item) => item.key === pendingKey && item.pending);
  if (at < 0) {
    return items;
  }
  const next = items.slice();
  next[at] = { ...items[at], turn };
  return next;
}

export function storedChatItems(conversation: XbotHistory): XbotChatItem[] {
  const entries = Array.isArray(conversation.messages) ? conversation.messages : [];
  return entries.map((entry, index): XbotChatItem => {
    const key = `stored-${conversation.localId}-${index}`;
    if (entry.role !== "assistant") {
      return { key, role: "user", content: entry.message };
    }
    return { key, role: "assistant", content: entry.message, message: xbotOf(entry) };
  });
}

// Drops any still-pending line; everything else keeps its key so paged tables stay paged.
export function beforeNewTurn(items: XbotChatItem[]): XbotChatItem[] {
  return items.filter((item) => !item.pending);
}

// If the pending line is gone (switched conversation, stopped), the answer is dropped rather than appended.
export function settleTurn(items: XbotChatItem[], pendingKey: string, settled: XbotChatItem | null): XbotChatItem[] {
  const at = items.findIndex((item) => item.key === pendingKey);
  if (at < 0) {
    return items;
  }
  const next = items.slice();
  if (settled) {
    next[at] = settled;
  } else {
    next.splice(at, 1);
  }
  return next;
}

/**
 * The thread without the line that answered a question, when that answer never reached the service (refused or
 * stopped): the question is then the newest answer again and can be answered again (QuestionBlock reopens).
 */
export function withoutAnswerLine(items: XbotChatItem[], pendingKey: string): XbotChatItem[] {
  const at = items.findIndex((item) => item.key === pendingKey);
  return at > 0 && items[at - 1].role === "user" ? [...items.slice(0, at - 1), ...items.slice(at)] : items;
}

/**
 * The thread after Stop: the pending line goes, and so does the person's line when it answered a question and the
 * turn never began (the service never took the answer), so the question can be answered again.
 */
export function afterStop(items: XbotChatItem[], pendingKey: string): XbotChatItem[] {
  const at = items.findIndex((item) => item.key === pendingKey);
  const unanswered = at > 0 && !items[at].turn && Boolean(items[at - 1].sent?.answerTo);
  return settleTurn(unanswered ? withoutAnswerLine(items, pendingKey) : items, pendingKey, null);
}

/** What an answer replied to, to be asked again: the person's line just before it, as sent, or null. */
export interface AskedBefore {
  outgoing: XbotOutgoing;
  /** The words the thread shows for it. */
  shown: string;
  /** The draft it came from, when the line was sent in this visit; absent for one read back from the history. */
  draft?: SentDraft;
}

/**
 * What an answer replied to: the person's line just before it, sent again exactly as it was - its picked objects and
 * the question it answered included. A line kept from an earlier visit has only its words, so only they go again.
 */
export function askedBefore(items: XbotChatItem[], key: string): AskedBefore | null {
  const at = items.findIndex((item) => item.key === key);
  for (let i = at - 1; i >= 0; i--) {
    const item = items[i];
    if (item.role === "user") {
      const shown = item.content.trim();
      if (item.sent) {
        return { outgoing: item.sent, shown, draft: item.draft };
      }
      return shown ? { outgoing: { text: shown }, shown } : null;
    }
  }
  return null;
}

/** The newest answer: the only one whose question can still be answered and whose suggestions still ask. */
export function lastAnswerKey(items: XbotChatItem[]): string {
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    if (item.role === "user") {
      return "";
    }
    if (item.role === "assistant" && item.message && !item.pending) {
      return item.key;
    }
  }
  return "";
}
